import { useState, useEffect, useRef } from 'react';
import { LOGO_FALLBACK, fmtDate, fmt$, today, WMO, WX_ICON } from '../lib/constants';
import { signOut, supabase } from '../lib/supabase';
import { useGoogle } from '../hooks/useGoogle';
import ProjectDetail from './ProjectDetail';
import { LogEntryModal } from './modals/LogPickerModal';
import AddTaskModal from './modals/AddTaskModal';
import AddPhotoModal from './modals/AddPhotoModal';
import AddDecisionModal from './modals/AddDecisionModal';
import InviteClient from './InviteClient';
import DecisionDetailModal from './modals/DecisionDetailModal';
import ClientPortal from './ClientPortal';
import DecisionTemplateLibrary from './DecisionTemplateLibrary';

const D = {
  bg: '#080808', surface: '#0F0F0F', elevated: '#1A1A1A', card: '#141414',
  border: '#242424', gold: '#9A732F', goldlt: '#F8DA6B',
  goldgrad: 'linear-gradient(135deg, #9A732F 0%, #F8DA6B 50%, #9A732F 100%)',
  muted: '#555555', subtle: '#2A2A2A', white: '#FFFFFF',
  offwhite: '#E8E4DC', green: '#4CAF7D', red: '#E74C3C',
  font: '"Avenir Next", Avenir, "Gill Sans", Calibri, sans-serif',
};

function GeneralTodosDesktop() {
  const [todos, setTodos] = useState([]);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('todos').select('*').is('project_id', null).eq('done', false).order('created_at', { ascending: false });
    if (data) setTodos(data);
  };

  useEffect(() => { load(); }, []);

  const completeTodo = async (id) => {
    await supabase.from('todos').update({ done: true, done_at: new Date().toISOString() }).eq('id', id);
    // Task created from a client checklist item → complete that item + notify
    const t = todos.find(x => x.id === id);
    if (t?.source_checklist_id) {
      await supabase.from('checklist_items').update({
        done: true, done_by: 'builder', done_at: new Date().toISOString(),
      }).eq('id', t.source_checklist_id);
      try {
        const { notifyProjectClients } = await import('../hooks/usePushNotifications');
        if (t.project_id) await notifyProjectClients(t.project_id, '✓ Completed from your list', (t.text || '').slice(0, 80), window.location.origin);
      } catch(e) {}
    }
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const addTodo = async () => {
    if (!newText.trim()) return;
    await supabase.from('todos').insert({ project_id: null, text: newText.trim(), priority: 'medium' });
    setNewText(''); setAdding(false); load();
  };

  return (
    <div style={{ background: D.card, border: `2px solid ${D.gold}`, borderRadius: 12, padding: 18, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: D.goldlt, fontSize: 14, fontWeight: 700 }}>General</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: D.muted, fontSize: 12 }}>{todos.length} open</span>
          <button onClick={() => setAdding(!adding)}
            style={{ background: D.goldlt, color: '#000', border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: D.font, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
        </div>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input autoFocus placeholder="New general task…" value={newText}
            onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTodo(); if (e.key === 'Escape') setAdding(false); }}
            style={{ flex: 1, background: D.elevated, border: `1px solid ${D.border}`, borderRadius: 7, padding: '7px 10px', color: D.white, fontSize: 13, outline: 'none', fontFamily: D.font }} />
          <button onClick={addTodo}
            style={{ background: D.goldlt, color: '#000', border: 'none', borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: D.font }}>Add</button>
        </div>
      )}
      {todos.length === 0 && !adding && <div style={{ color: D.muted, fontSize: 12 }}>No general tasks</div>}
      {todos.map(t => (
        <DesktopTaskRow key={t.id} task={t} onComplete={() => completeTodo(t.id)} onUpdate={load} />
      ))}
    </div>
  );
}

function ProjectTodosDesktop({ project, onOpenProject }) {
  const [todos, setTodos] = useState([]);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('todos').select('*').eq('project_id', project.id).eq('done', false).order('created_at', { ascending: false });
    if (data) setTodos(data);
  };

  useEffect(() => { load(); }, [project.id]);

  const completeTodo = async (id) => {
    await supabase.from('todos').update({ done: true, done_at: new Date().toISOString() }).eq('id', id);
    // Task created from a client checklist item → complete that item + notify
    const t = todos.find(x => x.id === id);
    if (t?.source_checklist_id) {
      await supabase.from('checklist_items').update({
        done: true, done_by: 'builder', done_at: new Date().toISOString(),
      }).eq('id', t.source_checklist_id);
      try {
        const { notifyProjectClients } = await import('../hooks/usePushNotifications');
        if (t.project_id) await notifyProjectClients(t.project_id, '✓ Completed from your list', (t.text || '').slice(0, 80), window.location.origin);
      } catch(e) {}
    }
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const addTodo = async () => {
    if (!newText.trim()) return;
    await supabase.from('todos').insert({ project_id: project.id, text: newText.trim(), priority: 'medium' });
    setNewText(''); setAdding(false); load();
  };

  return (
    <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 12, padding: 18, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: D.offwhite, fontSize: 14, fontWeight: 700, cursor: 'pointer' }} onClick={() => onOpenProject(project.id, 'todos')}>{project.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: D.muted, fontSize: 12 }}>{todos.length} open</span>
          <button onClick={() => setAdding(!adding)} style={{ background: D.goldlt, color: '#000', border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: D.font, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
        </div>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input autoFocus placeholder="New task…" value={newText} onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTodo(); if (e.key === 'Escape') setAdding(false); }}
            style={{ flex: 1, background: D.elevated, border: `1px solid ${D.border}`, borderRadius: 7, padding: '7px 10px', color: D.white, fontSize: 13, outline: 'none', fontFamily: D.font }} />
          <button onClick={addTodo} style={{ background: D.goldlt, color: '#000', border: 'none', borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: D.font }}>Add</button>
        </div>
      )}
      {todos.length === 0 && !adding && <div style={{ color: D.muted, fontSize: 12 }}>All caught up ✅</div>}
      {todos.map(t => (
        <DesktopTaskRow key={t.id} task={t} onComplete={() => completeTodo(t.id)} onUpdate={load} />
      ))}
    </div>
  );
}

export default function DesktopLayout({ projects, onOpenProject, onGoHome, onRefresh, isAssistant = false }) {
  const [view, setView] = useState('home');

  useEffect(() => {
    const onPopState = (e) => {
      const state = e.state;
      if (!state || state.view === 'home') {
        setView('home');
        setActiveProject(projects[0] || null);
        setActiveTab('overview');
      } else if (state.view === 'project' && state.projectId) {
        const p = projects.find(x => x.id === state.projectId || x.id === parseInt(state.projectId));
        if (p) { setActiveProject(p); setView('project'); setActiveTab(state.tab || 'overview'); }
      }
    };
    window.addEventListener('popstate', onPopState);
    if (!window.history.state) window.history.replaceState({ view: 'home' }, '');
    return () => window.removeEventListener('popstate', onPopState);
  }, [projects]);

  const [activeProject, setActiveProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showLogModal, setShowLogModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showPreBuildMaster, setShowPreBuildMaster] = useState(false);
  const [previewClientProject, setPreviewClientProject] = useState(null);
  const [calEvents, setCalEvents] = useState([]);
  const [weather, setWeather] = useState({});
  const { googleConnected, syncing, connectGoogle, syncCalendar, syncDecisions } = useGoogle();

  useEffect(() => {
    const COORDS = [
      { id: 1, lat: 35.9245, lng: -86.9028, name: 'Franklin, TN' },
      { id: 2, lat: 35.6151, lng: -86.9978, name: 'Columbia, TN' },
      { id: 3, lat: 35.8168, lng: -86.1372, name: 'Bradyville, TN' },
      { id: 5, lat: 36.0986, lng: -86.8219, name: 'Nashville, TN' },
    ];
    COORDS.forEach(async ({ id, lat, lng, name }) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
          `&current_weather=true&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
          `&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FChicago&forecast_days=1`
        );
        const data = await res.json();
        const cw = data.current_weather;
        if (!cw) return;
        setWeather(prev => ({
          ...prev,
          [id]: {
            temp: Math.round(cw.temperature),
            icon: WX_ICON[cw.weathercode] || '🌤',
            condition: WMO[cw.weathercode] || 'Weather',
            hi: data.daily?.temperature_2m_max?.[0] ? Math.round(data.daily.temperature_2m_max[0]) : null,
            lo: data.daily?.temperature_2m_min?.[0] ? Math.round(data.daily.temperature_2m_min[0]) : null,
            rain: data.daily?.precipitation_probability_max?.[0] || 0,
            forecastUrl: `https://forecast.weather.gov/MapClick.php?lat=${lat}&lon=${lng}`,
            location: name,
          }
        }));
      } catch(e) {}
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('calendar_events').select('*').order('event_date').order('event_time').limit(50);
      if (data) setCalEvents(data);
    };
    load();
  }, []);

  useEffect(() => {
    if (googleConnected && projects.length) {
      syncCalendar(projects).then(() => {
        supabase.from('calendar_events').select('*').order('event_date').order('event_time').limit(50).then(({ data }) => { if (data) setCalEvents(data); });
      });
      syncDecisions(projects);
    }
  }, [googleConnected, projects.length]);

  const openProject = (id, tab = 'overview') => {
    setActiveProject(projects.find(p => p.id === id || p.id === parseInt(id)));
    setActiveTab(tab);
    setView('project');
    window.history.pushState({ view: 'project', projectId: id, tab }, '');
  };

  const localDate = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };
  const days = [0, 1, 2].map(localDate);
  const dayLabels = {
    [days[0]]: 'Today',
    [days[1]]: 'Tomorrow',
    [days[2]]: new Date(days[2] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }),
  };

  const NAV = (
    <nav style={{ height: 64, background: D.surface, borderBottom: `1px solid ${D.border}`, display: 'flex', alignItems: 'center', padding: '0 32px', gap: 20, flexShrink: 0, position: 'sticky', top: 0, zIndex: 100 }}>
      <img src="/icons/icon-192.png" alt="Muller Homes" style={{ height: 40, cursor: 'pointer', borderRadius: 6 }} onClick={() => { setView('home'); window.history.pushState({ view: 'home' }, ''); }} />
      <div style={{ display: 'flex', gap: 4 }}>
        {projects.map(p => (
          <button key={p.id} onClick={() => openProject(p.id)}
            style={{ background: (view === 'project' && activeProject?.id === p.id) ? 'rgba(154,115,47,0.15)' : 'transparent', border: `1px solid ${(view === 'project' && activeProject?.id === p.id) ? D.gold : 'transparent'}`, borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontFamily: D.font, color: (view === 'project' && activeProject?.id === p.id) ? D.goldlt : D.muted, fontSize: 13, fontWeight: (view === 'project' && activeProject?.id === p.id) ? 700 : 400 }}>
            {p.name} <span style={{ marginLeft: 6, background: (view === 'project' && activeProject?.id === p.id) ? D.gold : D.subtle, color: (view === 'project' && activeProject?.id === p.id) ? '#000' : D.muted, borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{p.progress}%</span>
          </button>
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!isAssistant && <button onClick={() => setShowInviteModal(true)} style={{ background: 'transparent', border: `1px solid ${D.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.offwhite, fontFamily: D.font, fontSize: 12 }}>👤 Invite Client</button>}
        <button onClick={() => setShowTemplateLibrary(true)} style={{ background: 'transparent', border: `1px solid ${D.gold}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.goldlt, fontFamily: D.font, fontSize: 12, fontWeight: 600 }}>📋 Decision Library</button>
        <button onClick={() => setShowPreBuildMaster(true)} style={{ background: 'transparent', border: `1px solid ${D.gold}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.goldlt, fontFamily: D.font, fontSize: 12, fontWeight: 600 }}>⭐ Pre-Build Master</button>
        <button onClick={() => setShowTaskModal(true)} style={{ background: D.elevated, border: `1px solid ${D.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.offwhite, fontFamily: D.font, fontSize: 12 }}>+ Task</button>
        <button onClick={() => setShowDecisionModal(true)} style={{ background: D.elevated, border: `1px solid ${D.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.offwhite, fontFamily: D.font, fontSize: 12 }}>+ Decision</button>
        <button onClick={() => setShowLogModal(true)} style={{ background: D.goldgrad, border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', color: '#000', fontFamily: D.font, fontSize: 12, fontWeight: 700 }}>+ Log</button>
        {!googleConnected
          ? <button onClick={connectGoogle} style={{ background: 'transparent', border: `1px solid ${D.gold}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.goldlt, fontFamily: D.font, fontSize: 12 }}>Connect Google</button>
          : <div style={{ color: D.green, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 6, height: 6, borderRadius: '50%', background: D.green }} />{syncing ? 'Syncing…' : 'Google Connected'}</div>
        }
        <button onClick={() => signOut()} style={{ background: 'transparent', border: `1px solid ${D.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.muted, fontFamily: D.font, fontSize: 12 }}>Sign Out</button>
      </div>
    </nav>
  );

  if (view === 'home') return (
    <div style={{ minHeight: '100vh', background: D.bg, fontFamily: D.font, display: 'flex', flexDirection: 'column' }}>
      {NAV}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 28 }}>
            <div>
              <DesktopHomeNotifications projects={projects} />
              <div style={{ color: D.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14 }}>Schedule</div>
              {days.map(day => {
                const events = calEvents.filter(e => e.event_date === day).sort((a, b) => {
              const parseTime = (t) => {
                if (!t || t === 'All Day') return -1;
                const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
                if (!m) return 0;
                let h = parseInt(m[1]), min = parseInt(m[2]), ampm = m[3].toUpperCase();
                if (ampm === 'PM' && h !== 12) h += 12;
                if (ampm === 'AM' && h === 12) h = 0;
                return h * 60 + min;
              };
              return parseTime(a.event_time) - parseTime(b.event_time);
            });
                return (
                  <div key={day} style={{ marginBottom: 14 }}>
                    <div style={{ color: D.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{dayLabels[day]}</div>
                    {events.length === 0 ? (
                      <div style={{ background: D.card, borderRadius: 8, padding: '10px 14px', border: `1px solid ${D.border}`, color: D.muted, fontSize: 12 }}>Nothing scheduled</div>
                    ) : events.map((ev, i) => (
                      <div key={i} style={{ background: D.card, borderRadius: 8, padding: '10px 14px', marginBottom: 4, border: `1px solid ${D.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ color: D.goldlt, fontSize: 11, fontWeight: 700, minWidth: 60, borderRight: `1px solid ${D.gold}`, paddingRight: 10, flexShrink: 0 }}>{ev.event_time}</div>
                        <div style={{ color: D.white, fontSize: 13, fontWeight: 600, flex: 1 }}>{ev.summary}</div>
                        {ev.calendar_name && ev.calendar_name !== 'Personal' && <div style={{ background: D.elevated, color: D.muted, fontSize: 9, borderRadius: 4, padding: '2px 7px', flexShrink: 0 }}>{ev.calendar_name}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
              <DesktopNeededDecisions projects={projects} onOpenProject={openProject} />
            </div>

            <div>
              <div style={{ color: D.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14 }}>Tasks</div>
              <GeneralTodosDesktop />
              {projects.map(p => <ProjectTodosDesktop key={p.id} project={p} onOpenProject={openProject} />)}
            </div>

            <div>
              <div style={{ color: D.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14 }}>Active Builds</div>
              {projects.map(p => {
                const w = weather[parseInt(p.id)];
                return (
                  <div key={p.id} onClick={() => openProject(p.id)}
                    style={{ background: D.card, borderRadius: 14, padding: 20, marginBottom: 14, border: `1px solid ${D.border}`, cursor: 'pointer', transition: 'border-color 0.15s', position: 'relative', overflow: 'hidden' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = D.gold}
                    onMouseLeave={e => e.currentTarget.style.borderColor = D.border}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: `${p.progress}%`, height: 2, background: D.goldgrad }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <div style={{ color: D.white, fontSize: 16, fontWeight: 700, marginBottom: 2 }}>{p.name}</div>
                        <div style={{ color: D.muted, fontSize: 11 }}>{p.address}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: D.goldlt, fontSize: 28, fontWeight: 900, lineHeight: 1 }}>{p.progress}%</div>
                        <div style={{ color: D.muted, fontSize: 10 }}>{p.phase}</div>
                      </div>
                    </div>
                    <div style={{ background: D.subtle, borderRadius: 999, height: 4, marginBottom: 12 }}>
                      <div style={{ height: '100%', width: `${p.progress}%`, background: D.goldgrad, borderRadius: 999 }} />
                    </div>
                    {w && (
                      <a href={w.forecastUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, background: D.elevated, borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                        <span style={{ fontSize: 18 }}>{w.icon}</span>
                        <span style={{ color: D.white, fontSize: 13, fontWeight: 600 }}>{w.temp}°F · {w.condition}</span>
                        {w.hi && <span style={{ color: D.muted, fontSize: 11, marginLeft: 4 }}>H:{w.hi}° L:{w.lo}°</span>}
                        <span style={{ color: D.gold, fontSize: 9, fontWeight: 700, marginLeft: 'auto' }}>7-DAY →</span>
                      </a>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ color: D.muted, fontSize: 11 }}>{fmtDate(p.start_date)} → {fmtDate(p.est_complete)}</div>
                      <div style={{ color: D.goldlt, fontSize: 11, fontWeight: 600 }}>{fmt$(p.estimate)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showLogModal && <LogEntryModal projectId={projects[0]?.id} onClose={() => setShowLogModal(false)} onSuccess={onRefresh} />}
      {showTaskModal && <AddTaskModal projects={projects} onClose={() => setShowTaskModal(false)} onSuccess={onRefresh} />}
      {showPhotoModal && <AddPhotoModal projects={projects} onClose={() => setShowPhotoModal(false)} />}
      {showDecisionModal && <AddDecisionModal projects={projects} onClose={() => setShowDecisionModal(false)} onSuccess={onRefresh} />}
      {showInviteModal && <InviteClient projects={projects} onClose={() => setShowInviteModal(false)} />}
      {showTemplateLibrary && <DecisionTemplateLibrary projects={projects} onClose={() => setShowTemplateLibrary(false)} />}
      {showPreBuildMaster && <PreBuildMasterModal onClose={() => setShowPreBuildMaster(false)} />}

      {previewClientProject && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 390, height: '90vh', borderRadius: 40, overflow: 'hidden', boxShadow: '0 40px 80px rgba(0,0,0,0.6)', position: 'relative', border: '8px solid #222' }}>
            <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 120, height: 24, background: '#222', borderRadius: '0 0 16px 16px', zIndex: 10 }} />
            <div style={{ height: '100%', overflowY: 'auto' }}>
              <ClientPortal previewProjectId={previewClientProject} />
            </div>
          </div>
          <button onClick={() => setPreviewClientProject(null)}
            style={{ position: 'fixed', top: 24, right: 24, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', color: '#fff', fontSize: 14, fontFamily: D.font }}>
            ✕ Close Preview
          </button>
          <div style={{ position: 'fixed', bottom: 24, display: 'flex', gap: 10 }}>
            {projects.map(p => (
              <button key={p.id} onClick={() => setPreviewClientProject(p.id)}
                style={{ background: previewClientProject === p.id ? D.goldlt : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: previewClientProject === p.id ? '#000' : '#fff', fontSize: 12, fontWeight: 700, fontFamily: D.font }}>
                {p.name.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: D.bg, fontFamily: D.font, display: 'flex', flexDirection: 'column' }}>
      {NAV}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', paddingBottom: 40 }}>
          {activeProject && (
            <ProjectDetail
              project={activeProject}
              allProjects={projects}
              tab={activeTab}
              setTab={setActiveTab}
              onGoHome={() => { setView('home'); window.history.pushState({ view: 'home' }, ''); }}
              hideBackButton={false}
              onUpdateProject={() => {}}
              onRefresh={onRefresh}
            />
          )}
        </div>
      </div>
      {showLogModal && <LogEntryModal projectId={activeProject?.id} onClose={() => setShowLogModal(false)} onSuccess={onRefresh} />}
      {showTaskModal && <AddTaskModal projects={projects} defaultProjectId={activeProject?.id} onClose={() => setShowTaskModal(false)} onSuccess={onRefresh} />}
      {showPhotoModal && <AddPhotoModal projects={projects} defaultProjectId={activeProject?.id} onClose={() => setShowPhotoModal(false)} />}
      {showDecisionModal && <AddDecisionModal projects={projects} defaultProjectId={activeProject?.id} onClose={() => setShowDecisionModal(false)} onSuccess={onRefresh} />}
      {showInviteModal && <InviteClient projects={projects} onClose={() => setShowInviteModal(false)} />}
      {showTemplateLibrary && <DecisionTemplateLibrary projects={projects} onClose={() => setShowTemplateLibrary(false)} />}
      {showPreBuildMaster && <PreBuildMasterModal onClose={() => setShowPreBuildMaster(false)} />}
    </div>
  );
}

function DesktopNeededDecisions({ projects, onOpenProject }) {
  const [decisions, setDecisions] = useState([]);
  const [selectedDecision, setSelectedDecision] = useState(null);

  useEffect(() => {
    supabase.from('decisions').select('*').in('status', ['pending', 'pending_confirmation']).order('due_date', { ascending: true }).limit(20)
      .then(({ data }) => { if (data) setDecisions(data); });
  }, []);

  if (decisions.length === 0) return null;

  const getPriority = (due_date) => {
    if (!due_date) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const days = Math.round((new Date(due_date + 'T12:00:00') - today) / 86400000);
    if (days < 0) return { label: 'Past Due', color: '#E74C3C' };
    if (days <= 2) return { label: 'Urgent', color: '#E67E22' };
    if (days <= 7) return { label: 'This Week', color: '#F8DA6B' };
    return null;
  };

  const byProject = projects.map(p => ({
    project: p,
    decisions: decisions.filter(d => parseInt(d.project_id) === parseInt(p.id)),
  })).filter(g => g.decisions.length > 0);

  if (byProject.length === 0) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ color: D.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14 }}>
        Decisions · {decisions.length}
      </div>
      {byProject.map(({ project: p, decisions: pDecs }) => (
        <div key={p.id} style={{ marginBottom: 16 }}>
          <div style={{ color: D.white, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, paddingBottom: 4, borderBottom: `1px solid ${D.border}` }}>
            {p.name.split(' ')[0]} · {pDecs.length}
          </div>
          {pDecs.map(d => {
            const priority = getPriority(d.due_date);
            return (
              <div key={d.id} onClick={() => setSelectedDecision(d)}
                style={{ background: D.card, borderRadius: 8, padding: '10px 14px', marginBottom: 5, border: `1px solid ${priority?.color || D.border}`, borderLeft: `3px solid ${priority?.color || D.gold}`, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: D.white, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.item}</div>
                    {d.category && <div style={{ color: D.gold, fontSize: 10, marginTop: 1 }}>{d.category}</div>}
                  </div>
                  <div style={{ flexShrink: 0, marginLeft: 10, textAlign: 'right' }}>
                    {d.status === 'pending_confirmation' && <div style={{ color: '#4FA3E0', fontSize: 10, fontWeight: 700 }}>Awaiting ✓</div>}
                    {priority && <div style={{ color: priority.color, fontSize: 10, fontWeight: 700 }}>{priority.label}</div>}
                    {d.due_date && <div style={{ color: D.muted, fontSize: 10 }}>{new Date(d.due_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {selectedDecision && (
        <DecisionDetailModal
          decision={selectedDecision}
          isBuilder={true}
          onClose={() => setSelectedDecision(null)}
          onSuccess={() => { setSelectedDecision(null); supabase.from('decisions').select('*').in('status',['pending','pending_confirmation']).order('due_date',{ascending:true}).limit(20).then(({data})=>{if(data)setDecisions(data);}); }}
        />
      )}
    </div>
  );
}

function DesktopTaskRow({ task: t, onComplete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.text);
  const [priority, setPriority] = useState(t.priority || 'medium');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await supabase.from('todos').update({ text: text.trim(), priority }).eq('id', t.id);
    setSaving(false); setEditing(false); onUpdate();
  };

  const handleDelete = async () => {
    await supabase.from('todos').delete().eq('id', t.id);
    onUpdate();
  };

  if (editing) return (
    <div style={{ background: '#1A1A2E', borderRadius: 8, padding: 12, marginBottom: 8, border: `1px solid ${D.gold}` }}>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus
        style={{ width: '100%', background: D.bg, border: 'none', borderRadius: 6, padding: '8px 10px', color: D.offwhite, fontSize: 13, outline: 'none', fontFamily: D.font, boxSizing: 'border-box', resize: 'none', marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {['low', 'medium', 'high'].map(p => (
          <button key={p} onClick={() => setPriority(p)}
            style={{ flex: 1, background: priority === p ? D.goldlt : D.card, color: priority === p ? '#000' : D.muted, border: 'none', borderRadius: 5, padding: '4px', cursor: 'pointer', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>
            {p}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ flex: 1, background: D.goldlt, color: '#000', border: 'none', borderRadius: 6, padding: '7px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={() => setEditing(false)}
          style={{ flex: 1, background: 'transparent', border: `1px solid ${D.border}`, borderRadius: 6, padding: '7px', cursor: 'pointer', color: D.muted, fontSize: 12 }}>
          Cancel
        </button>
        <button onClick={handleDelete}
          style={{ background: 'transparent', border: '1px solid #E74C3C', borderRadius: 6, padding: '7px 10px', cursor: 'pointer', color: '#E74C3C', fontSize: 12 }}>
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${D.subtle}` }}>
      <button onClick={e => { e.stopPropagation(); onComplete(); }}
        style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${D.gold}`, background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
      <div style={{ flex: 1, color: D.offwhite, fontSize: 13, cursor: 'pointer' }} onClick={() => setEditing(true)}>{t.text}</div>
      {t.priority && <div style={{ color: t.priority === 'high' ? D.goldlt : D.muted, fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>{t.priority}</div>}
    </div>
  );
}


function DesktopHomeNotifications({ projects }) {
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await supabase.from('app_notifications').select('*')
      .eq('recipient_role', 'builder').eq('read', false)
      .order('created_at', { ascending: false }).limit(50);
    if (data) setItems(data);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await supabase.from('app_notifications').update({ read: true }).eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const markAllRead = async () => {
    await supabase.from('app_notifications').update({ read: true }).eq('recipient_role', 'builder').eq('read', false);
    setItems([]);
  };

  const projectName = (pid) => projects.find(p => parseInt(p.id) === parseInt(pid))?.name?.split(' ')[0] || '';

  if (items.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: D.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase' }}>🔔 Notifications · {items.length}</div>
        {items.length > 1 && (
          <button onClick={markAllRead}
            style={{ background: 'transparent', border: `1px solid ${D.border}`, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', color: D.muted, fontSize: 10, fontWeight: 700, fontFamily: D.font }}>
            ✓ Mark all read
          </button>
        )}
      </div>
      {items.map(n => (
        <div key={n.id} style={{ background: D.card, borderRadius: 8, padding: '10px 14px', marginBottom: 5, border: `1px solid ${D.border}`, borderLeft: `3px solid ${D.goldlt}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0, cursor: n.url ? 'pointer' : 'default' }}
            onClick={() => { if (n.url) { markRead(n.id); setTimeout(() => { window.location.href = n.url; }, 120); } }}>
            {projectName(n.project_id) && <div style={{ color: D.muted, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{projectName(n.project_id)}</div>}
            <div style={{ color: D.white, fontSize: 13, fontWeight: 700 }}>{n.title}</div>
            {n.body && <div style={{ color: D.offwhite, fontSize: 12, lineHeight: 1.45 }}>{n.body}</div>}
            <div style={{ color: D.muted, fontSize: 10, marginTop: 3 }}>
              {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
          </div>
          <button onClick={() => markRead(n.id)}
            style={{ background: 'rgba(76,175,61,0.1)', border: '1px solid #4CAF7D', borderRadius: 8, padding: '5px 11px', cursor: 'pointer', color: '#4CAF7D', fontSize: 10.5, fontWeight: 700, fontFamily: D.font, flexShrink: 0 }}>
            ✓ Read
          </button>
        </div>
      ))}
    </div>
  );
}


function PreBuildMasterModal({ onClose }) {
  const CATEGORIES = [
    'Exterior', 'Interior', 'Foundation', 'Concrete', 'Framing', 'Fireplaces', 'Windows/Doors',
    'Plumbing', 'Electrical', 'HVAC', 'Tile', 'Flooring', 'Trim', 'Paint/Stain', 'Appliances', 'Finishes',
  ];
  const [master, setMaster] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [answerType, setAnswerType] = useState('options');
  const [options, setOptions] = useState(['', '']);
  const [costList, setCostList] = useState(['', '']);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [followUps, setFollowUps] = useState({});
  const [dragId, setDragId] = useState(null);
  const [order, setOrder] = useState([]);
  const rowRefs = useRef({});

  const setFU = (i, patch) => setFollowUps(prev => ({ ...prev, [i]: { ...(prev[i] || { name: '', type: 'text', opts: ['', ''] }), ...patch } }));
  const removeFU = (i) => setFollowUps(prev => { const n = { ...prev }; delete n[i]; return n; });

  const load = async () => {
    const { data } = await supabase.from('prebuild_master').select('*').order('sort');
    if (data) setMaster(data);
  };
  useEffect(() => { load(); }, []);

  const tops = master.filter(m => !m.parent_master_id);
  useEffect(() => { setOrder(tops); }, [master.map(m => m.id).join(',')]);

  const dragStart = (id) => (e) => { e.stopPropagation(); e.preventDefault(); setDragId(id); };
  const dragMove = (e) => {
    if (!dragId) return;
    const y = e.clientY;
    const cur = order.findIndex(t => t.id === dragId);
    if (cur < 0) return;
    let target = cur;
    for (let i = 0; i < order.length; i++) {
      if (i === cur) continue;
      const el = rowRefs.current[order[i].id];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (i < cur && y < midY) { target = i; break; }
      if (i > cur && y > midY) target = i;
    }
    if (target !== cur) {
      const n = [...order];
      const [moved] = n.splice(cur, 1);
      n.splice(target, 0, moved);
      setOrder(n);
    }
  };
  const dragEnd = async () => {
    if (dragId) {
      const changed = order.some((t, i) => t.id !== tops[i]?.id);
      if (changed) {
        for (let i = 0; i < order.length; i++) {
          await supabase.from('prebuild_master').update({ sort: i + 1 }).eq('id', order[i].id);
        }
        load();
      }
    }
    setDragId(null);
  };

  const groupByCategory = async () => {
    const rank = (c) => { const i = CATEGORIES.indexOf(c); return i === -1 ? 999 : i; };
    const sorted = [...order].sort((a, b) => rank(a.category) - rank(b.category));
    for (let i = 0; i < sorted.length; i++) {
      await supabase.from('prebuild_master').update({ sort: i + 1 }).eq('id', sorted[i].id);
    }
    load();
  };

  const addOption = () => { if (options.length < 5) { setOptions([...options, '']); setCostList([...costList, '']); } };
  const removeOption = (i) => { if (options.length > 2) { setOptions(options.filter((_, idx) => idx !== i)); setCostList(costList.filter((_, idx) => idx !== i)); } };
  const updateOption = (i, val) => setOptions(options.map((o, idx) => idx === i ? val : o));
  const [attachList, setAttachList] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [dragHover, setDragHover] = useState(false);

  const uploadAttachment = async (file) => {
    if (!file) return;
    setAttaching(true);
    try {
      const path = `files/prebuild_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error } = await supabase.storage.from('purchase-photos').upload(path, file);
      if (!error) {
        const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
        setAttachList(prev => [...prev, { name: file.name, url: data.publicUrl }]);
      }
    } catch(e) { console.error('Attachment upload failed:', e); }
    setAttaching(false);
  };

  useEffect(() => {
    if (!showForm) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            uploadAttachment(new File([file], `pasted_photo_${Date.now()}.${ext}`, { type: file.type }));
          }
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [showForm]);

  const resetForm = () => { setName(''); setCategory(''); setAnswerType('options'); setOptions(['', '']); setCostList(['', '']); setComments(''); setFollowUps({}); setEditingId(null); setAttachList([]); };

  const openEdit = (d) => {
    setEditingId(d.id);
    setName(d.item || '');
    setCategory(d.category && CATEGORIES.includes(d.category) ? d.category : '');
    const hasOpts = Array.isArray(d.options) && d.options.length > 0;
    setAnswerType(hasOpts ? 'options' : 'text');
    setOptions(hasOpts ? [...d.options] : ['', '']);
    setCostList(hasOpts ? d.options.map(o => (d.option_costs && d.option_costs[o] != null) ? String(d.option_costs[o]) : '') : ['', '']);
    setComments(d.notes || '');
    setAttachList(Array.isArray(d.attachments) ? [...d.attachments] : []);
    const fu = {};
    if (hasOpts) {
      master.filter(m => m.parent_master_id === d.id).forEach(ch => {
        const oi = d.options.indexOf(ch.parent_option);
        if (oi >= 0) fu[oi] = { name: ch.item, type: Array.isArray(ch.options) && ch.options.length ? 'options' : 'text', opts: Array.isArray(ch.options) && ch.options.length ? [...ch.options] : ['', ''] };
      });
    }
    setFollowUps(fu);
    setShowForm(true);
  };

  const buildCosts = () => {
    const m = {};
    options.forEach((o, i) => {
      const v = parseFloat((costList[i] || '').toString().replace(/[^0-9.]/g, ''));
      if (o.trim() && !isNaN(v) && v > 0) m[o.trim()] = v;
    });
    return Object.keys(m).length ? m : null;
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const validOptions = options.filter(o => o.trim());
    if (editingId) {
      await supabase.from('prebuild_master').update({
        item: name.trim(), category: category || null, notes: comments.trim() || null,
        options: answerType === 'options' ? validOptions : null,
        option_costs: answerType === 'options' ? buildCosts() : null,
        attachments: attachList.length ? attachList : null,
      }).eq('id', editingId);
      // reconcile children
      const existing = master.filter(m => m.parent_master_id === editingId);
      const wanted = {};
      if (answerType === 'options') {
        for (const [iStr, fu] of Object.entries(followUps)) {
          const optVal = validOptions[parseInt(iStr)];
          if (optVal && fu.name?.trim()) wanted[optVal] = fu;
        }
      }
      for (const ch of existing) {
        const want = wanted[ch.parent_option];
        if (!want) await supabase.from('prebuild_master').delete().eq('id', ch.id);
        else {
          await supabase.from('prebuild_master').update({
            item: want.name.trim(),
            options: want.type === 'options' ? want.opts.filter(o => o.trim()) : null,
          }).eq('id', ch.id);
          delete wanted[ch.parent_option];
        }
      }
      for (const [optVal, fu] of Object.entries(wanted)) {
        await supabase.from('prebuild_master').insert({
          item: fu.name.trim(), category: category || null,
          options: fu.type === 'options' ? fu.opts.filter(o => o.trim()) : null,
          sort: 999, parent_master_id: editingId, parent_option: optVal,
        });
      }
    } else {
      const maxSort = master.reduce((m, d) => Math.max(m, d.sort || 0), 0);
      const { data: parent } = await supabase.from('prebuild_master').insert({
        item: name.trim(), category: category || null, notes: comments.trim() || null,
        options: answerType === 'options' ? validOptions : null,
        option_costs: answerType === 'options' ? buildCosts() : null,
        attachments: attachList.length ? attachList : null, sort: maxSort + 1,
      }).select().single();
      if (parent && answerType === 'options') {
        for (const [iStr, fu] of Object.entries(followUps)) {
          const optVal = validOptions[parseInt(iStr)];
          if (!optVal || !fu.name?.trim()) continue;
          await supabase.from('prebuild_master').insert({
            item: fu.name.trim(), category: category || null,
            options: fu.type === 'options' ? fu.opts.filter(o => o.trim()) : null,
            sort: maxSort + 1, parent_master_id: parent.id, parent_option: optVal,
          });
        }
      }
    }
    setSaving(false); resetForm(); setShowForm(false);
    load();
  };

  const remove = async (d) => {
    for (const ch of master.filter(m => m.parent_master_id === d.id)) {
      await supabase.from('prebuild_master').delete().eq('id', ch.id);
    }
    await supabase.from('prebuild_master').delete().eq('id', d.id);
    load();
  };

  const inputStyle = {
    width: '100%', background: D.elevated, border: `1px solid ${D.border}`,
    borderRadius: 9, padding: '10px 12px', color: D.white, fontSize: 13,
    outline: 'none', fontFamily: D.font, boxSizing: 'border-box',
  };
  const labelStyle = { color: D.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6, display: 'block' };
  const canSave = name.trim() && (answerType === 'text' || options.filter(o => o.trim()).length >= 2);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onMouseMove={dragId ? dragMove : undefined} onMouseUp={dragId ? dragEnd : undefined}>
      <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: 16, width: 680, maxHeight: '88vh', display: 'flex', flexDirection: 'column', fontFamily: D.font }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${D.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: D.goldlt, fontSize: 15, fontWeight: 800 }}>⭐ Pre-Build Master List</div>
            <div style={{ color: D.muted, fontSize: 11, marginTop: 2 }}>Click a question to edit · drag ⠿ to reorder · load into any build from its Pre-Build tab</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: D.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>

          {order.length > 1 && !showForm && (
            <button onClick={groupByCategory}
              style={{ width: '100%', background: 'transparent', border: `1px solid ${D.border}`, borderRadius: 11, padding: 10, cursor: 'pointer', color: D.muted, fontSize: 12, fontWeight: 700, fontFamily: D.font, marginBottom: 10 }}>
              ⚡ Group by Category
            </button>
          )}

          {!showForm ? (
            <button onClick={() => { resetForm(); setShowForm(true); }}
              style={{ width: '100%', background: 'rgba(248,218,107,0.08)', border: `1px dashed ${D.gold}`, borderRadius: 11, padding: 12, cursor: 'pointer', color: D.goldlt, fontSize: 13, fontWeight: 700, fontFamily: D.font, marginBottom: 16 }}>
              + Add Master Question
            </button>
          ) : (
            <div style={{ background: D.card, borderRadius: 14, padding: 18, marginBottom: 16, border: `1px solid ${D.gold}` }}>
              <div style={{ color: D.goldlt, fontSize: 13, fontWeight: 800, marginBottom: 12 }}>{editingId ? '✎ Edit Question' : '+ New Question'}</div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Decision Name *</label>
                <input autoFocus placeholder="e.g. Master bath tile selection" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Category</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setCategory(category === c ? '' : c)}
                      style={{ background: category === c ? D.goldlt : D.elevated, color: category === c ? '#000' : D.offwhite, border: `1px solid ${category === c ? D.goldlt : D.border}`, borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: category === c ? 700 : 400, fontFamily: D.font }}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Answer Type *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[{ val: 'options', label: '📋 Options', sub: 'List 2–5 choices' }, { val: 'text', label: '✏️ Text Box', sub: 'Free-form answer' }].map(t => (
                    <button key={t.val} onClick={() => setAnswerType(t.val)}
                      style={{ background: answerType === t.val ? 'rgba(154,115,47,0.2)' : D.elevated, border: `2px solid ${answerType === t.val ? D.goldlt : D.border}`, borderRadius: 12, padding: '14px 12px', cursor: 'pointer', fontFamily: D.font, textAlign: 'center' }}>
                      <div style={{ color: answerType === t.val ? D.goldlt : D.white, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{t.label}</div>
                      <div style={{ color: D.muted, fontSize: 11 }}>{t.sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              {answerType === 'options' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Options (2–5)</label>
                  {options.map((opt, i) => (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input placeholder={`Option ${i + 1}`} value={opt} onChange={e => updateOption(i, e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                        <input placeholder="$ approx" value={costList[i] || ''} inputMode="decimal"
                          onChange={e => setCostList(costList.map((x, xi) => xi === i ? e.target.value : x))}
                          style={{ ...inputStyle, width: 90, flex: 'none', color: D.goldlt, fontSize: 12 }} />
                        <button onClick={() => followUps[i] ? removeFU(i) : setFU(i, {})}
                          title="If they pick this, ask a follow-up"
                          style={{ background: followUps[i] ? 'rgba(79,163,224,0.15)' : D.elevated, border: `1px solid ${followUps[i] ? '#4FA3E0' : D.border}`, borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: followUps[i] ? '#4FA3E0' : D.muted, fontSize: 13, fontWeight: 700 }}>↳</button>
                        {options.length > 2 && (
                          <button onClick={() => { removeOption(i); removeFU(i); }} style={{ background: D.elevated, border: `1px solid ${D.border}`, borderRadius: 8, padding: '0 12px', cursor: 'pointer', color: D.muted, fontSize: 16 }}>×</button>
                        )}
                      </div>
                      {followUps[i] && (
                        <div style={{ marginLeft: 16, marginTop: 8, padding: 12, background: 'rgba(79,163,224,0.05)', border: '1px solid #2A4258', borderRadius: 10 }}>
                          <div style={{ color: '#4FA3E0', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>↳ If they pick "{opt || `option ${i + 1}`}", then ask (required):</div>
                          <input placeholder="Follow-up question" value={followUps[i].name || ''} onChange={e => setFU(i, { name: e.target.value })}
                            style={{ ...inputStyle, marginBottom: 8 }} />
                          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                            {[{ v: 'options', l: '📋 Options' }, { v: 'text', l: '✏️ Text' }].map(t => (
                              <button key={t.v} onClick={() => setFU(i, { type: t.v })}
                                style={{ flex: 1, background: (followUps[i].type || 'text') === t.v ? 'rgba(154,115,47,0.2)' : D.elevated, border: `1px solid ${(followUps[i].type || 'text') === t.v ? D.goldlt : D.border}`, borderRadius: 8, padding: 8, cursor: 'pointer', color: (followUps[i].type || 'text') === t.v ? D.goldlt : D.muted, fontSize: 11.5, fontWeight: 700, fontFamily: D.font }}>
                                {t.l}
                              </button>
                            ))}
                          </div>
                          {(followUps[i].type || 'text') === 'options' && (followUps[i].opts || ['', '']).map((fo, fi) => (
                            <input key={fi} placeholder={`Choice ${fi + 1}`} value={fo}
                              onChange={e => setFU(i, { opts: (followUps[i].opts || ['', '']).map((x, xi) => xi === fi ? e.target.value : x) })}
                              style={{ ...inputStyle, marginBottom: 6 }} />
                          ))}
                          {(followUps[i].type || 'text') === 'options' && (followUps[i].opts || []).length < 5 && (
                            <button onClick={() => setFU(i, { opts: [...(followUps[i].opts || ['', '']), ''] })}
                              style={{ background: 'transparent', border: `1px dashed ${D.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: D.muted, fontSize: 11, fontFamily: D.font, width: '100%' }}>+ Choice</button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {options.length < 5 && (
                    <button onClick={addOption} style={{ background: 'transparent', border: `1px dashed ${D.border}`, borderRadius: 9, padding: '8px 16px', cursor: 'pointer', color: D.muted, fontSize: 12, fontFamily: D.font, width: '100%' }}>+ Add Option</button>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Comments</label>
                <textarea placeholder="Notes, context clients will see…" value={comments} onChange={e => setComments(e.target.value)} rows={3}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>📎 Attachments {attachList.length > 0 ? `(${attachList.length})` : ''} <span style={{ color: D.muted, fontSize: 9, letterSpacing: 0, textTransform: 'none' }}>(clients see these on the question)</span></label>
                {attachList.map((a, i) => {
                  const isImg = /\.(png|jpe?g|gif|webp|heic)($|\?)/i.test(a.url) || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.name || '');
                  return (
                    <div key={i} style={{ background: D.elevated, border: `1px solid ${D.border}`, borderRadius: 9, padding: '8px 11px', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13 }}>{isImg ? '🖼' : '📎'}</span>
                        <a href={a.url} target="_blank" rel="noreferrer"
                          style={{ flex: 1, color: '#4FA3E0', fontSize: 12, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</a>
                        <button onClick={() => setAttachList(prev => prev.filter((_, xi) => xi !== i))}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: D.muted, fontSize: 14, padding: 0 }}>×</button>
                      </div>
                      {isImg && (
                        <a href={a.url} target="_blank" rel="noreferrer">
                          <img src={a.url} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginTop: 8, display: 'block' }} />
                        </a>
                      )}
                    </div>
                  );
                })}
                <label
                  onDragOver={e => { e.preventDefault(); setDragHover(true); }}
                  onDragLeave={() => setDragHover(false)}
                  onDrop={e => { e.preventDefault(); setDragHover(false); Array.from(e.dataTransfer.files || []).forEach(fl => uploadAttachment(fl)); }}
                  style={{ display: 'block', border: `1px dashed ${dragHover ? D.goldlt : D.border}`, background: dragHover ? 'rgba(248,218,107,0.06)' : 'transparent', borderRadius: 10, padding: '9px 14px', cursor: 'pointer', color: dragHover ? D.goldlt : D.muted, fontSize: 12, textAlign: 'center' }}>
                  📎 {attaching ? 'Uploading…' : 'Attach a file — drag & drop, paste, or click'}
                  <input type="file" multiple style={{ display: 'none' }}
                    onChange={e => { Array.from(e.target.files || []).forEach(fl => uploadAttachment(fl)); e.target.value = ''; }} />
                </label>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={!canSave || saving}
                  style={{ flex: 1, background: canSave ? D.goldlt : D.subtle, color: canSave ? '#000' : D.muted, border: 'none', borderRadius: 10, padding: 12, cursor: canSave ? 'pointer' : 'default', fontSize: 13, fontWeight: 800, fontFamily: D.font }}>
                  {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Save Question'}
                </button>
                <button onClick={() => { resetForm(); setShowForm(false); }}
                  style={{ background: 'transparent', border: `1px solid ${D.border}`, borderRadius: 10, padding: '12px 18px', cursor: 'pointer', color: D.muted, fontSize: 13, fontFamily: D.font }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {order.length === 0 && !showForm && (
            <div style={{ color: D.muted, fontSize: 12, textAlign: 'center', padding: 30 }}>No master questions yet — build your standard set once, reuse forever.</div>
          )}
          {order.map((d, i) => (
            <div key={d.id}>
            {(i === 0 || (order[i - 1].category || '') !== (d.category || '')) && !dragId && (
              <div style={{ color: D.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', margin: i === 0 ? '4px 0 8px' : '14px 0 8px' }}>
                {d.category || 'Uncategorized'}
              </div>
            )}
            <div ref={el => rowRefs.current[d.id] = el}
              style={{ background: D.card, borderRadius: 10, padding: '10px 14px', marginBottom: 6, border: `1px solid ${dragId === d.id ? D.goldlt : D.border}`, display: 'flex', alignItems: 'center', gap: 12, opacity: dragId === d.id ? 0.5 : 1 }}>
              <div onMouseDown={dragStart(d.id)}
                style={{ color: D.muted, fontSize: 15, cursor: 'grab', userSelect: 'none', padding: '2px 2px' }}>⠿</div>
              <div style={{ color: D.muted, fontSize: 11, fontWeight: 800, minWidth: 24 }}>{i + 1}.</div>
              <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openEdit(d)}>
                <div style={{ color: D.white, fontSize: 13, fontWeight: 600 }}>{d.item}{master.some(m => m.parent_master_id === d.id) && <span style={{ color: '#4FA3E0', fontSize: 9, marginLeft: 6 }}>↳ follow-up</span>}</div>
                <div style={{ color: D.muted, fontSize: 10, marginTop: 2 }}>
                  {d.category ? `${d.category} · ` : ''}{Array.isArray(d.options) && d.options.length > 0 ? d.options.map(o => d.option_costs && d.option_costs[o] ? `${o} (~$${Number(d.option_costs[o]).toLocaleString()})` : o).join(' · ') : 'Text answer'}
                </div>
              </div>
              <button onClick={() => remove(d)}
                style={{ background: 'transparent', border: '1px solid #4A2A2A', borderRadius: 6, padding: '0 9px', cursor: 'pointer', color: '#E74C3C', fontSize: 12 }}>×</button>
            </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
